import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import manual from '../../Docs/MANUAL_GYM_OWNERS.md?raw';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { BookOpen, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ManualSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const slugify = (title: string) =>
    title
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

interface ManualSection {
    id: string;
    title: string;
}

const parseSections = (content: string): ManualSection[] => {
    const sections: ManualSection[] = [];
    for (const line of content.split(/\r?\n/)) {
        const match = line.match(/^##\s+(.+)$/);
        if (match) {
            const title = match[1].trim();
            sections.push({ id: slugify(title) || `seccion-${sections.length}`, title });
        }
    }
    return sections;
};

export function ManualSheet({ open, onOpenChange }: ManualSheetProps) {
    const sections = useMemo(() => parseSections(manual), []);
    const scrollRef = useRef<HTMLDivElement>(null);
    const headingRefs = useRef<Map<string, HTMLElement>>(new Map());
    const [activeId, setActiveId] = useState<string>('');
    const [showTopBtn, setShowTopBtn] = useState(false);

    useEffect(() => {
        if (open) {
            requestAnimationFrame(() => {
                if (scrollRef.current) scrollRef.current.scrollTop = 0;
                setActiveId(sections[0]?.id ?? '');
                setShowTopBtn(false);
            });
        }
    }, [open, sections]);

    useEffect(() => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;

        const onScroll = () => {
            const offset = scrollEl.scrollTop;
            setShowTopBtn(offset > 300);

            let current = '';
            for (const section of sections) {
                const el = headingRefs.current.get(section.id);
                if (el && el.offsetTop <= offset + 120) current = section.id;
            }
            if (!current) current = sections[0]?.id ?? '';
            setActiveId(current);
        };

        scrollEl.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => scrollEl.removeEventListener('scroll', onScroll);
    }, [sections]);

    const scrollToSection = (id: string) => {
        const scrollEl = scrollRef.current;
        const el = headingRefs.current.get(id);
        if (scrollEl && el) {
            const containerTop = scrollEl.getBoundingClientRect().top;
            const targetTop = el.getBoundingClientRect().top;
            scrollEl.scrollTo({
                top: scrollEl.scrollTop + (targetTop - containerTop) - 96,
                behavior: 'smooth',
            });
        }
        setActiveId(id);
    };

    const scrollTop = () => {
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const components = {
        h2: ({ children }: any) => {
            const id = slugify(String(children ?? '').trim()) || `seccion-${sections.length}`;
            return (
                <h2
                    id={id}
                    ref={(el) => {
                        if (el) headingRefs.current.set(id, el);
                    }}
                    className="scroll-mt-28 text-xl font-black tracking-tight text-foreground pt-2"
                >
                    {children}
                </h2>
            );
        },
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                className="p-0 w-full sm:max-w-xl md:max-w-2xl flex flex-col gap-0 bg-background"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border pr-12 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <BookOpen className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <SheetTitle className="text-lg font-black">Manual de Uso</SheetTitle>
                            <SheetDescription className="text-xs font-medium">
                                Guía completa de GymPro SV para tu gimnasio
                            </SheetDescription>
                        </div>
                    </div>
                </SheetHeader>

                <div className="md:hidden px-6 py-3 border-b border-border bg-muted/30 shrink-0">
                    <Select value={activeId} onValueChange={(val) => scrollToSection(val)}>
                        <SelectTrigger className="h-10 rounded-xl bg-background border-border text-sm font-bold">
                            <SelectValue placeholder="Ir a sección…" />
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border-none shadow-2xl glass max-h-[50vh]">
                            {sections.map((section) => (
                                <SelectItem key={section.id} value={section.id} className="font-medium text-sm pr-8">
                                    {section.title}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex flex-1 min-h-0 relative">
                    <nav className="hidden md:flex w-52 shrink-0 border-r border-border overflow-y-auto p-3 flex-col gap-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground px-3 pb-2">
                            Índice
                        </p>
                        {sections.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => scrollToSection(section.id)}
                                className={cn(
                                    "w-full text-left px-3 py-1.5 rounded-lg text-xs font-bold transition-all",
                                    section.id === activeId
                                        ? "bg-primary/10 text-primary"
                                        : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                                )}
                            >
                                {section.title}
                            </button>
                        ))}
                    </nav>

                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-primary/20 p-6 lg:p-8"
                    >
                        <article className="prose prose-slate dark:prose-invert prose-sm max-w-none prose-headings:font-black prose-h2:border-b prose-h2:border-border prose-h2:pb-2 prose-h2:mt-8 prose-p:leading-relaxed prose-strong:font-bold prose-a:text-primary prose-blockquote:border-primary/40 prose-blockquote:text-foreground/80 prose-table:text-sm prose-th:font-black prose-th:bg-muted/50 prose-th:p-2 prose-td:p-2">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={components as any}>
                                {manual}
                            </ReactMarkdown>
                        </article>

                        {showTopBtn && (
                            <button
                                onClick={scrollTop}
                                className="fixed bottom-6 right-6 z-10 h-10 w-10 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:scale-105 transition-transform"
                                aria-label="Volver arriba"
                            >
                                <ChevronUp className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}